import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { Button } from '@shared/components/Button';
import { SectionList } from '@shared/components/SectionList';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { getSubtableRowNumber, getSubtableRowSlots } from './subtableColumns';
import { getRowPressProps } from './subtableRowPress';
import { SubtableRowRefs } from './useSubtableRows';
import styles from './SubtableList.module.css';

interface Props {
  columns: API.AnyComponent[];
  rows: API.WorkflowEntityFragment[];
  refs?: SubtableRowRefs;
  /** The row open in the row menu, highlighted. */
  selectedRowId?: string;
  onRowPress: (entityId: string, index: number) => void;
  /**
   * Set by SubtableTableModal: every row, scrolling, loading more near the end. Without it, the
   * card form's preview: the first rows only, no scrolling, and "Show N more" for the rest.
   */
  modal?: boolean;
  /** Modal: whether another page can load, and loading it. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Card form: every row the subtable has (the "N" in "Show N more"), and showing them all. */
  totalCount?: number;
  onShowMore?: () => void;
}

/** The row's min-height (7Q, SubtableList.module.css), for rows the virtualizer hasn't measured. */
const ESTIMATED_ROW_HEIGHT = 56;
/** Rows the card form shows before "Show N more". */
const PREVIEW_ROW_COUNT = 4;

/**
 * Mobile: each row collapses to its number, a title, a secondary line and a trailing value. In the
 * card form it previews the first few rows; in the expanded modal it scrolls every row, virtualized,
 * loading the next page as it nears the end.
 */
export function SubtableList({
  columns,
  rows,
  refs,
  selectedRowId,
  onRowPress,
  modal = false,
  hasMore = false,
  onLoadMore,
  totalCount = rows.length,
  onShowMore,
}: Props) {
  const shownRows = useMemo(() => (modal ? rows : rows.slice(0, PREVIEW_ROW_COUNT)), [modal, rows]);
  const sections = useMemo(() => [{ data: shownRows }], [shownRows]);
  const moreCount = modal ? 0 : totalCount - shownRows.length;
  const hasShowMore = moreCount > 0;
  return (
    <SectionList<API.WorkflowEntityFragment>
      role="list"
      className={[styles.subtableList, modal && styles.modal]}
      sections={sections}
      keyExtractor={row => row.id}
      estimatedItemHeight={ESTIMATED_ROW_HEIGHT}
      onEndReached={modal && hasMore ? onLoadMore : undefined}
      ListFooterComponent={
        hasShowMore && (
          <View className={styles.showMore}>
            <Button
              variant="ghost"
              bold
              size="medium"
              label={i18n('card.blocks.subtableInput.showMore', { count: moreCount })}
              onClick={onShowMore}
            />
          </View>
        )
      }
      renderItem={({ item: row, index }) => (
        <SubtableListRow
          row={row}
          index={index}
          isLast={!hasShowMore && index === shownRows.length - 1}
          columns={columns}
          refs={refs}
          onRowPress={onRowPress}
          selectedRowId={selectedRowId}
        />
      )}
    />
  );
}

function SubtableListRow({
  row,
  index,
  columns,
  refs,
  isLast,
  onRowPress,
  selectedRowId,
}: Pick<Props, 'columns' | 'refs' | 'selectedRowId' | 'onRowPress'> & {
  row: API.WorkflowEntityFragment;
  index: number;
  isLast: boolean;
}) {
  const { title, secondary, trailing } = getSubtableRowSlots(row, columns, refs);
  return (
    <View
      row
      gap={1.5}
      role="listitem"
      {...getRowPressProps(() => onRowPress(row.id, index))}
      className={[styles.row, isLast && styles.last, row.id === selectedRowId && styles.selected]}
    >
      <Text size="body2" inactive className={styles.rowNumber}>
        {getSubtableRowNumber(index)}
      </Text>
      <View className={styles.text}>
        <Text size="body1" numberOfLines={1}>
          {title}
        </Text>
        {!isEmpty(secondary) && (
          <Text size="body2" inactive numberOfLines={1}>
            {secondary}
          </Text>
        )}
      </View>
      {!isEmpty(trailing) && (
        <Text size="body1" weight="semibold" className={styles.trailing}>
          {trailing}
        </Text>
      )}
    </View>
  );
}
